// Port of bootstrap-vue's modal manager (node_modules/bootstrap-vue/src/components/modal/helpers/modal-manager.js).
// Keeps the list of open modals in opening order and from it sets on every registered modal `zIndex` (base + index),
// `isTop` (only the top modal enforces focus), `scrollbarWidth` and `isBodyOverflowing`, and the page state:
// `modal-open` and `data-modal-open-count` on body, and padding for the hidden scrollbar.

const DEFAULT_ZINDEX = 1040
const SELECTOR_FIXED_CONTENT = '.fixed-top, .fixed-bottom, .is-fixed, .sticky-top'
const SELECTOR_STICKY_CONTENT = '.sticky-top'
const SELECTOR_NAVBAR_TOGGLER = '.navbar-toggler'

const toInteger = (value, fallback) => {
  const integer = parseInt(value, 10)
  return isNaN(integer) ? fallback : integer
}

const toFloat = (value, fallback) => {
  const float = parseFloat(value)
  return isNaN(float) ? fallback : float
}

export const createModalManager = () => {
  const modals = []
  let baseZIndex = null
  let scrollbarWidth = null
  let isBodyOverflowing = false
  let paddingChanged = []
  let marginChanged = []

  // z-index of .modal-backdrop in the app's CSS
  const getBaseZIndex = () => {
    if (baseZIndex === null) {
      const div = document.createElement('div')
      div.className = 'modal-backdrop d-none'
      div.style.display = 'none'
      document.body.appendChild(div)
      baseZIndex = toInteger(getComputedStyle(div).zIndex, DEFAULT_ZINDEX)
      document.body.removeChild(div)
    }
    return baseZIndex || DEFAULT_ZINDEX
  }

  const getScrollbarWidth = () => {
    if (scrollbarWidth === null) {
      const div = document.createElement('div')
      div.className = 'modal-scrollbar-measure'
      document.body.appendChild(div)
      scrollbarWidth = div.getBoundingClientRect().width - div.clientWidth
      document.body.removeChild(div)
    }
    return scrollbarWidth || 0
  }

  const checkScrollbar = () => {
    const { left, right } = document.body.getBoundingClientRect()
    isBodyOverflowing = left + right < window.innerWidth
  }

  // move fixed / sticky content and the body by the width of the scrollbar that modal-open hides
  // (style properties are set through setProperty / removeProperty: jsdom ignores `style.x = ''`)
  const shift = (el, property, attribute, sign, list) => {
    el.setAttribute(attribute, el.style.getPropertyValue(property))
    el.style.setProperty(property, `${toFloat(getComputedStyle(el).getPropertyValue(property), 0) + sign * getScrollbarWidth()}px`)
    list.push(el)
  }

  const setScrollbar = () => {
    if (!isBodyOverflowing) return
    document.querySelectorAll(SELECTOR_FIXED_CONTENT).forEach(el => shift(el, 'padding-right', 'data-padding-right', 1, paddingChanged))
    document.querySelectorAll(SELECTOR_STICKY_CONTENT).forEach(el => shift(el, 'margin-right', 'data-margin-right', -1, marginChanged))
    document.querySelectorAll(SELECTOR_NAVBAR_TOGGLER).forEach(el => shift(el, 'margin-right', 'data-margin-right', 1, marginChanged))
    shift(document.body, 'padding-right', 'data-padding-right', 1, [])
  }

  const restore = (el, property, attribute) => {
    if (!el.hasAttribute(attribute)) return
    const value = el.getAttribute(attribute)
    if (value) el.style.setProperty(property, value)
    else el.style.removeProperty(property)
    el.removeAttribute(attribute)
  }

  const resetScrollbar = () => {
    paddingChanged.forEach(el => restore(el, 'padding-right', 'data-padding-right'))
    marginChanged.forEach(el => restore(el, 'margin-right', 'data-margin-right'))
    paddingChanged = []
    marginChanged = []
    restore(document.body, 'padding-right', 'data-padding-right')
  }

  const updateModals = () => {
    modals.forEach((modal, index) => {
      modal.zIndex = getBaseZIndex() + index
      modal.scrollbarWidth = getScrollbarWidth()
      modal.isTop = index === modals.length - 1
      modal.isBodyOverflowing = isBodyOverflowing
    })
  }

  const resetModal = (modal) => {
    modal.zIndex = getBaseZIndex()
    modal.isTop = true
    modal.isBodyOverflowing = false
  }

  // what bootstrap-vue's watchers on the modal count and on the modal list do, in their order
  const changed = (oldCount) => {
    const newCount = modals.length
    getScrollbarWidth()
    if (newCount > 0 && oldCount === 0) {
      checkScrollbar()
      setScrollbar()
      document.body.classList.add('modal-open')
    } else if (newCount === 0 && oldCount > 0) {
      resetScrollbar()
      document.body.classList.remove('modal-open')
    }
    document.body.setAttribute('data-modal-open-count', String(newCount))
    checkScrollbar()
    requestAnimationFrame(updateModals)
  }

  const registerModal = (modal) => {
    if (modals.includes(modal)) return
    modals.push(modal)
    changed(modals.length - 1)
  }

  const unregisterModal = (modal) => {
    const index = modals.indexOf(modal)
    if (index === -1) return
    modals.splice(index, 1)
    resetModal(modal)
    changed(modals.length + 1)
  }

  return {
    getBaseZIndex,
    registerModal,
    unregisterModal
  }
}

let current = null

// the one manager all modals share
export const getModalManager = () => {
  if (!current) current = createModalManager()
  return current
}
